// Servicio profesional de sincronización Local ↔ Firebase
// Implementa estrategia "Offline First" con resolución de conflictos
// VERSIÓN OFFLINE-FIRST - V2 CON COMPANYID E INDEXEDDB

import { db, isFirebaseEnabled, shouldUseFirebase } from './config';

// ============================================
// DETECCIÓN DE ESTADO DE RED
// ============================================
const isOnline = () => {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
};

// ============================================
// INDEXEDDB WRAPPER PARA COLA DE SYNC
// ============================================
class IndexedDBQueue {
  constructor() {
    this.dbName = 'imoveis_sync_db';
    this.version = 1;
    this.storeName = 'sync_queue';
    this.db = null;
  }

  async init() {
    if (this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => {
        console.warn('⚠️ IndexedDB no disponible, usando localStorage fallback');
        this.db = null;
        reject(new Error('IndexedDB not available'));
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        console.log('✅ IndexedDB inicializada para cola de sync');
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('by_status', 'status');
          store.createIndex('by_timestamp', 'timestamp');
        }
      };
    });
  }

  async getAll() {
    if (!this.db) {
      // Fallback a localStorage
      try {
        const saved = localStorage.getItem('firebase_sync_queue');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async save(queue) {
    if (!this.db) {
      // Fallback a localStorage
      try {
        localStorage.setItem('firebase_sync_queue', JSON.stringify(queue));
      } catch (error) {
        console.warn('⚠️ No se pudo guardar cola:', error.message);
      }
      return;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Limpiar store y guardar todo
      store.clear();
      queue.forEach(item => store.put(item));
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async clear() {
    if (!this.db) {
      localStorage.removeItem('firebase_sync_queue');
      return;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      store.clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

class SyncService {
  constructor() {
    this.isOnline = isOnline();
    this.firebaseAvailable = !!(db && isFirebaseEnabled() && this.isOnline);
    this.syncQueue = [];
    this.isSyncing = false;
    this.lastSyncTime = null;
    this.syncListeners = [];
    this.conflictListeners = [];
    this.activeConflicts = new Map();
    this.queueStore = new IndexedDBQueue();
    
    // Inicializar IndexedDB
    this.queueStore.init().catch(err => {
      console.log('ℹ️ Usando localStorage como fallback para cola de sync');
    });
    
    // Detectar cambios en conexión
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());
    }
    
    console.log(`🔄 SyncService V2 iniciado - Modo: ${this.isOnline ? 'ONLINE' : 'OFFLINE'} | Firebase: ${this.firebaseAvailable ? '✅' : '❌'} | Storage: IndexedDB`);
  }
  
  // ===== DETECCIÓN DE CONEXIÓN =====
  
  handleOnline() {
    console.log('🌐 Conexión restablecida - Verificando Firebase...');
    this.isOnline = true;
    this.firebaseAvailable = !!(db && isFirebaseEnabled() && this.isOnline);
    
    if (this.firebaseAvailable) {
      console.log('📡 Firebase disponible - Iniciando sincronización...');
      this.processSyncQueue();
    } else {
      console.log('⚠️ Firebase no disponible - Modo local');
    }
    
    this.notifyListeners('online');
  }
  
  handleOffline() {
    console.log('📴 Modo offline activado - Los cambios se guardarán localmente');
    this.isOnline = false;
    this.firebaseAvailable = false;
    this.notifyListeners('offline');
  }
  
  // ===== COLA DE SINCRONIZACIÓN =====

  addToQueue(operation) {
    // Extraer metadata del documento si existe
    let clientVersion = 1;
    let clientLastModified = new Date().toISOString();
    
    // Si es una operación UPDATE, intentar obtener versión actual
    if (operation.type === 'UPDATE' && operation.docId) {
      try {
        const versionKey = `doc_version_${operation.collection}_${operation.docId}`;
        const cachedVersion = localStorage.getItem(versionKey);
        
        if (cachedVersion) {
          const versionData = JSON.parse(cachedVersion);
          clientVersion = versionData.version || 1;
          clientLastModified = versionData.lastModified || new Date().toISOString();
        }
      } catch (error) {
        console.warn('⚠️ No se pudo obtener metadata de versión:', error.message);
      }
    }
    
    const queueItem = {
      id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retryCount: 0,
      
      clientVersion,
      clientLastModified,
      
      ...operation
    };
    
    this.syncQueue.push(queueItem);
    this.saveQueueToStorage();
    
    console.log(`📝 Operación añadida a cola: ${operation.type} - ${operation.collection} (Company: ${operation.companyId || 'N/A'})`);
    
    // ✅ SOLO PROCESA SI ESTÁ ONLINE Y FIREBASE DISPONIBLE
    if (this.isOnline && this.firebaseAvailable) {
      this.processSyncQueue();
    } else {
      console.log('⏳ Operación guardada localmente (modo offline)');
    }
    
    return queueItem.id;
  }
  
  async saveQueueToStorage() {
    try {
      await this.queueStore.save(this.syncQueue);
    } catch (error) {
      console.warn('⚠️ No se pudo guardar cola:', error.message);
      // Fallback a localStorage
      try {
        localStorage.setItem('firebase_sync_queue', JSON.stringify(this.syncQueue));
      } catch (e) {
        console.warn('⚠️ Fallback localStorage también falló:', e.message);
      }
    }
  }
  
  async loadQueueFromStorage() {
    try {
      const savedQueue = await this.queueStore.getAll();
      if (savedQueue && savedQueue.length > 0) {
        this.syncQueue = savedQueue;
        console.log(`📂 Cola cargada desde IndexedDB: ${this.syncQueue.length} items pendientes`);
      }
    } catch (error) {
      console.warn('⚠️ Error cargando desde IndexedDB, intentando localStorage:', error.message);
      // Fallback a localStorage
      try {
        const savedQueue = localStorage.getItem('firebase_sync_queue');
        if (savedQueue) {
          this.syncQueue = JSON.parse(savedQueue);
          console.log(`📂 Cola cargada desde localStorage: ${this.syncQueue.length} items pendientes`);
        }
      } catch (e) {
        console.warn('⚠️ Error cargando cola desde storage:', e.message);
      }
    }
  }
  
  // ===== PROCESAMIENTO DE LA COLA =====
  
  async processSyncQueue() {
    // ✅ VERIFICACIÓN CRÍTICA: Solo procesar si todo está listo
    if (this.isSyncing || !this.isOnline || !this.firebaseAvailable || this.syncQueue.length === 0) {
      if (!this.isOnline) console.log('⏸️ Sync pausado: offline');
      if (!this.firebaseAvailable) console.log('⏸️ Sync pausado: Firebase no disponible');
      return;
    }
    
    this.isSyncing = true;
    console.log(`🔄 Procesando cola de sincronización: ${this.syncQueue.length} items`);
    
    const successfulOps = [];
    const failedOps = [];
    
    for (const item of this.syncQueue.filter(op => op.status === 'pending')) {
      try {
        await this.executeSyncOperation(item);
        item.status = 'completed';
        item.completedAt = new Date().toISOString();
        successfulOps.push(item.id);
        
        console.log(`✅ Sincronizado: ${item.type} - ${item.collection}`);
      } catch (error) {
        item.status = 'failed';
        item.error = error.message;
        item.retryCount = (item.retryCount || 0) + 1;
        failedOps.push({ id: item.id, error: error.message });
        
        console.warn(`❌ Error sincronizando ${item.type}:`, error.message);
        
        // Backoff exponencial para reintentos
        if (item.retryCount > 5) {
          item.status = 'irrecoverable';
          console.log(`⚠️ Operación ${item.id} marcada como irrecoverable después de 5 intentos`);
        }
      }
    }
    
    // Filtrar operaciones completadas/irrecoverables
    this.syncQueue = this.syncQueue.filter(item => 
      item.status === 'pending' || item.status === 'failed'
    );
    
    await this.saveQueueToStorage();
    this.isSyncing = false;
    this.lastSyncTime = new Date().toISOString();
    
    if (successfulOps.length > 0 || failedOps.length > 0) {
      this.notifyListeners('sync-completed', {
        successful: successfulOps.length,
        failed: failedOps.length,
        pending: this.syncQueue.length
      });
    }
    
    return {
      successful: successfulOps.length,
      failed: failedOps.length,
      pending: this.syncQueue.length
    };
  }
  
  // ===== EJECUCIÓN DE OPERACIONES =====

  async executeSyncOperation(operation) {
    const { collection, type, data, docId, companyId, userId } = operation;
    
    // ✅ VERIFICACIÓN: Solo ejecutar si Firebase está disponible
    if (!db || !this.firebaseAvailable) {
      throw new Error('Firestore no disponible');
    }
    
    // ✅ VERIFICACIÓN: companyId requerido para operaciones multi-tenant
    if (!companyId && collection !== 'users' && collection !== 'companies') {
      console.warn('⚠️ Operación sin companyId, intentando extraer de data:', data);
      // Intentar extraer de data como fallback
      const extractedCompanyId = data?.companyId || operation.data?.companyId;
      if (!extractedCompanyId) {
        throw new Error('companyId requerido para operaciones multi-tenant');
      }
      operation.companyId = extractedCompanyId;
    }
    
    // ✅ Importación condicional (solo cuando hay internet)
    const { doc, setDoc, updateDoc, deleteDoc, collection: colRef, getDoc } = await import('firebase/firestore');
    
    // Construir ruta con companyId para colecciones multi-tenant
    const getCollectionPath = (collName) => {
      const globalCollections = ['users', 'companies', 'public_properties', 'public', 'audit_logs'];
      if (globalCollections.includes(collName)) {
        return collName;
      }
      return `companies/${operation.companyId}/${collName}`;
    };
    
    const collectionPath = getCollectionPath(collection);
    
    switch (type) {
      case 'CREATE':
        const newDocRef = doc(colRef(db, collectionPath));
        const createData = {
          ...data,
          companyId: operation.companyId || data?.companyId,
          _syncedAt: new Date().toISOString(),
          _syncStatus: 'synced',
          _localId: operation.localId,
          _createdAt: data?.createdAt || new Date().toISOString(),
          _version: 1,
          _lastModified: new Date().toISOString(),
          _modifiedBy: userId || 'system'
        };
        await setDoc(newDocRef, createData);
        
        const newVersionKey = `doc_version_${collection}_${newDocRef.id}`;
        const createVersionData = {
          version: 1,
          lastModified: createData._lastModified,
          syncedAt: createData._syncedAt
        };
        localStorage.setItem(newVersionKey, JSON.stringify(createVersionData));
        
        break;
        
      case 'UPDATE':
        const docRef = doc(db, collectionPath, docId);
        const docSnapshot = await getDoc(docRef);
        
        // Detección de conflicto
        let conflictDetected = false;
        let conflictDetails = null;
        
        if (docSnapshot.exists()) {
          const serverDoc = docSnapshot.data();
          const serverVersion = serverDoc._version || 1;
          const clientVersion = operation.clientVersion || 1;
          
          if (clientVersion < serverVersion) {
            conflictDetected = true;
            conflictDetails = {
              type: 'version_conflict',
              serverVersion,
              clientVersion,
              serverLastModified: serverDoc._lastModified,
              clientLastModified: operation.clientLastModified,
              operationId: operation.id,
              collection,
              docId,
              operation: 'UPDATE',
              timestamp: new Date().toISOString(),
              conflictId: `conflict-${collection}-${docId}-${Date.now()}`
            };
            
            console.warn(`⚠️ CONFLICTO DETECTADO en ${collection}/${docId}`);
            
            this.activeConflicts.set(conflictDetails.conflictId, {
              ...conflictDetails,
              resolved: false,
              resolutionStrategy: null,
              resolvedAt: null
            });
            
            this.notifyConflictListeners(conflictDetails);
          }
        }
        
        const updateData = {
          ...data,
          _syncedAt: new Date().toISOString(),
          _syncStatus: 'synced',
          _updatedAt: new Date().toISOString(),
          _version: (docSnapshot.exists() ? (docSnapshot.data()._version || 1) : 1) + 1,
          _lastModified: new Date().toISOString(),
          _modifiedBy: userId || 'system',
          _conflictResolved: conflictDetected,
          ...(conflictDetected && { 
            _conflictDetails: conflictDetails,
            _previousVersion: docSnapshot.exists() ? docSnapshot.data() : null
          })
        };
        
        await updateDoc(docRef, updateData);
        
        const versionKey = `doc_version_${collection}_${docId}`;
        const updateVersionData = {
          version: updateData._version,
          lastModified: updateData._lastModified,
          syncedAt: updateData._syncedAt
        };
        localStorage.setItem(versionKey, JSON.stringify(updateVersionData));
        
        break;
        
      case 'DELETE':
        const deleteRef = doc(db, collectionPath, docId);
        await deleteDoc(deleteRef);
        
        const deleteVersionKey = `doc_version_${collection}_${docId}`;
        localStorage.removeItem(deleteVersionKey);
        
        break;
        
      default:
        throw new Error(`Tipo de operación no soportada: ${type}`);
    }
  }
  
  // ===== API PÚBLICA PARA LA APLICACIÓN =====
  
  async syncCreate(collection, data, companyId = null, userId = null) {
    const localId = `local-${Date.now()}`;
    const operation = {
      type: 'CREATE',
      collection,
      data,
      localId,
      companyId: companyId || data?.companyId,
      userId: userId || data?.createdBy,
      createdAt: new Date().toISOString()
    };
    
    return this.addToQueue(operation);
  }
  
  async syncUpdate(collection, docId, updates, companyId = null, userId = null) {
    const operation = {
      type: 'UPDATE',
      collection,
      docId,
      data: updates,
      companyId: companyId || updates?.companyId,
      userId: userId || updates?.updatedBy,
      updatedAt: new Date().toISOString()
    };
    
    return this.addToQueue(operation);
  }
  
  async syncDelete(collection, docId, companyId = null, userId = null) {
    const operation = {
      type: 'DELETE',
      collection,
      docId,
      companyId,
      userId,
      deletedAt: new Date().toISOString()
    };
    
    return this.addToQueue(operation);
  }
  
  // ===== ESTADO Y MONITOREO =====
  
  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      firebaseAvailable: this.firebaseAvailable,
      isSyncing: this.isSyncing,
      queueLength: this.syncQueue.length,
      pending: this.syncQueue.filter(op => op.status === 'pending').length,
      failed: this.syncQueue.filter(op => op.status === 'failed').length,
      lastSyncTime: this.lastSyncTime,
      activeConflicts: this.activeConflicts.size,
      storageType: this.queueStore.db ? 'IndexedDB' : 'localStorage'
    };
  }
  
  getQueueStats() {
    const stats = {
      total: this.syncQueue.length,
      byType: {},
      byStatus: {},
      byCollection: {}
    };
    
    this.syncQueue.forEach(item => {
      stats.byType[item.type] = (stats.byType[item.type] || 0) + 1;
      stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
      stats.byCollection[item.collection] = (stats.byCollection[item.collection] || 0) + 1;
    });
    
    return stats;
  }
  
  async clearQueue() {
    const count = this.syncQueue.length;
    this.syncQueue = [];
    await this.queueStore.clear();
    localStorage.removeItem('firebase_sync_queue');
    console.log(`🧹 Cola limpiada: ${count} items eliminados`);
    return count;
  }
  
  retryFailed() {
    const failedItems = this.syncQueue.filter(item => item.status === 'failed');
    failedItems.forEach(item => {
      item.status = 'pending';
      item.retryCount = 0;
      delete item.error;
    });
    
    this.saveQueueToStorage();
    this.processSyncQueue();
    
    return failedItems.length;
  }
  
  // ===== LISTENERS =====
  
  addListener(callback) {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(listener => listener !== callback);
    };
  }
  
  notifyListeners(event, data = null) {
    this.syncListeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.warn('⚠️ Error en listener de sync:', error);
      }
    });
  }
  
  addConflictListener(callback) {
    this.conflictListeners.push(callback);
    
    this.activeConflicts.forEach(conflict => {
      if (!conflict.resolved) {
        setTimeout(() => callback(conflict), 0);
      }
    });
    
    return () => {
      this.conflictListeners = this.conflictListeners.filter(listener => listener !== callback);
    };
  }
  
  notifyConflictListeners(conflictData) {
    this.conflictListeners.forEach(listener => {
      try {
        listener({
          ...conflictData,
          id: conflictData.conflictId,
          resolved: false
        });
      } catch (error) {
        console.warn('⚠️ Error en listener de conflictos:', error);
      }
    });
  }
  
  resolveConflict(conflictId, resolutionStrategy = 'overwrite') {
    if (!this.activeConflicts.has(conflictId)) {
      console.warn(`⚠️ Conflicto no encontrado: ${conflictId}`);
      return false;
    }
    
    const conflict = this.activeConflicts.get(conflictId);
    
    this.activeConflicts.set(conflictId, {
      ...conflict,
      resolved: true,
      resolutionStrategy,
      resolvedAt: new Date().toISOString()
    });
    
    console.log(`✅ Conflicto resuelto: ${conflictId}`);
    return true;
  }
  
  getActiveConflicts() {
    return Array.from(this.activeConflicts.values()).filter(c => !c.resolved);
  }
  
  clearResolvedConflicts() {
    let removed = 0;
    
    this.activeConflicts.forEach((conflict, conflictId) => {
      if (conflict.resolved) {
        this.activeConflicts.delete(conflictId);
        removed++;
      }
    });
    
    console.log(`🧹 Conflictos resueltos eliminados: ${removed}`);
    return removed;
  }
  
  // ===== INICIALIZACIÓN =====
  
  async initialize() {
    await this.loadQueueFromStorage();
    
    // ✅ Solo procesa si está online Y Firebase disponible
    if (this.isOnline && this.firebaseAvailable && this.syncQueue.length > 0) {
      setTimeout(() => this.processSyncQueue(), 2000);
    } else {
      console.log('⏳ SyncService inicializado en modo local');
    }
    
    return this.getSyncStatus();
  }
}

// Singleton pattern
const syncService = new SyncService();
export default syncService;